<template>
    <Multiselect
        v-model="selectedOrder"
        :options="orders"
        :custom-label="formatOrderLabel"
        :loading="isLoading"
        :searchable="true"
        :class="customClass"
        :placeholder="placeholder"
        :close-on-select="true"
        label="ref"
        track-by="id"
        @search-change="searchQuery = $event"
    >
        <template #caret="{ isOpen }">
            <SvgIcon
                name="system-chevron-down"
                class="absolute right-0 top-1/2 mr-2 size-[18px] -translate-y-1/2 text-muval-gray-1 transition-transform duration-300"
                :class="{ 'rotate-180': isOpen }"
            />
        </template>
    </Multiselect>

    <div
        v-if="errorMessage || fetchError"
        class="mt-3 text-sm text-red-500"
    >
        {{ errorMessage || fetchError }}
    </div>
</template>

<script setup lang="ts">
import Multiselect from 'vue-multiselect';
import { watchDebounced } from '@vueuse/core';
import { useTimesheetStore } from '@store/timesheet';

interface OrderOption {
    id: number;
    ref: string;
    customer_name: string;
    route: string;
}

const props = defineProps({
    vid: {
        type: String,
        required: true,
    },
    class: {
        type: String,
        required: false,
        default: '',
    },
    placeholder: {
        type: String,
        required: false,
        default: 'Select an order',
    },
    initialOrderId: {
        type: Number,
        required: false,
        default: null,
    },
});

const timesheet = useTimesheetStore();

const vid = toRef(props, 'vid');
const { value: inputValue, errorMessage } = useField<number | null>(vid, undefined, {
    syncVModel: true,
});

const orders = ref<OrderOption[]>([]);
const isLoading = ref(false);
const searchQuery = ref('');
const fetchError = ref('');

// Request tracking for concurrency safety
let lastRequestId = 0;

// Custom class handling
const customClass = computed(() => {
    return props.class || 'max-w-xs'; // Default width if no class provided
});

// Computed property to handle conversion between ID and full object
const selectedOrder = computed({
    get: () => {
        if (inputValue.value) {
            return orders.value.find((order) => order.id === inputValue.value) || null;
        }
        return null;
    },
    set: (newValue) => {
        if (newValue && typeof newValue === 'object' && 'id' in newValue) {
            inputValue.value = newValue.id;
        } else {
            inputValue.value = null;
        }
    },
});

// Format order label for display
const formatOrderLabel = (order: OrderOption) => {
    return `#${order.ref} — ${order.customer_name}`;
};

// Debounced search function
watchDebounced(
    searchQuery,
    async (query) => {
        if (query && query.length > 0) {
            await fetchOrders(query);
        } else {
            await fetchOrders('');
        }
    },
    {
        debounce: 500,
    },
);

onMounted(async () => {
    await fetchOrders('', props.initialOrderId ? [props.initialOrderId] : []);
});

const fetchOrders = async (query?: string, includeIds: number[] = []) => {
    fetchError.value = '';
    isLoading.value = true;

    const requestId = ++lastRequestId;

    try {
        const searchParams: any = {};
        if (query && query.length > 0) searchParams.keyword = query;
        if (includeIds.length > 0) searchParams.include_ids = includeIds;

        const resp = await timesheet.searchOrder(searchParams);

        if (requestId !== lastRequestId) return;

        const list = resp?.data?.data ?? [];
        orders.value = Array.isArray(list) ? list : [];
    } catch (error: any) {
        if (requestId !== lastRequestId) return;

        fetchError.value = error?.response?.data?.message ?? 'Search failed. Please try again.';
        orders.value = [];
    } finally {
        if (requestId === lastRequestId) {
            isLoading.value = false;
        }
    }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
/* Custom styling for smaller text and proper positioning */
:deep(.multiselect) {
    @apply text-sm;
}

:deep(.multiselect__tags) {
    @apply text-sm;
}

:deep(.multiselect__single) {
    @apply mb-0 mt-[3px] text-xs;
}

:deep(.multiselect__input) {
    @apply text-sm;
}

:deep(.multiselect__placeholder) {
    @apply pl-[5px] pt-0 text-sm;
}

:deep(.multiselect__option) {
    @apply text-sm;
}

/* Fix dropdown positioning to prevent layout shift */
:deep(.multiselect) {
    @apply relative;
}

/* Let vue-multiselect decide whether to open above or below.
   We only ensure it spans the full width and has a high stacking context. */
:deep(.multiselect__content-wrapper) {
    @apply left-0 right-0 z-50;
}

:deep(.multiselect__tags) {
    @apply max-h-max;
}

/* Option styling */
:deep(.multiselect__option--highlight) {
    @apply bg-[#eaf5ff] text-muval-gray-1 outline-0;
}
:deep(.multiselect__option--highlight:after) {
    @apply hidden bg-[#f0f0f0] text-[#f0f0f0];
}
:deep(.multiselect__option--selected) {
    @apply bg-muval-gray-5 text-sm font-semibold text-muval-gray-1;
}
:deep(.multiselect__option--selected.multiselect__option--highlight) {
    @apply bg-muval-gray-5 text-muval-gray-1;
}
:deep(.multiselect__option--selected.multiselect__option--highlight:after) {
    @apply bg-muval-gray-5 text-muval-gray-1;
}
</style>
